import { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { put } from './api.js';

/**
 * Theme engine. The ramp for each accent is generated at runtime and two colours
 * are tuned for contrast: the ink on primary buttons, and the accent used for
 * links and active navigation. Hues like amber and teal are illegible at a fixed
 * lightness, so the lightness is moved until the contrast clears 4.5:1.
 */
export const THEMES = [
  { id: 'sevenmentor', name: 'SevenMentor Orange', desc: 'The company palette, taken from the logo', h: 25, s: 80, tag: 'Default' },
  { id: 'ocean', name: 'Ocean Blue', desc: 'Calm and low-contrast — easy on long days', h: 212, s: 72 },
  { id: 'teal', name: 'Mentor Teal', desc: 'The accent from the logo eyes', h: 184, s: 64 },
  { id: 'emerald', name: 'Emerald', desc: 'Fresh and legible in bright offices', h: 157, s: 56 },
  { id: 'violet', name: 'Violet', desc: 'Softer contrast, good for evening shifts', h: 262, s: 60 },
  { id: 'rose', name: 'Rose', desc: 'Warm without being loud', h: 344, s: 62 },
  { id: 'amber', name: 'Amber', desc: 'High energy — pairs well with dark mode', h: 38, s: 88 },
  { id: 'graphite', name: 'Graphite', desc: 'Almost no colour. Data does the talking', h: 220, s: 12 },
];

export const MODES = [
  { id: 'light', name: 'Light' },
  { id: 'dark', name: 'Dark' },
  { id: 'system', name: 'Match device' },
];
export const DENSITIES = [
  { id: 'comfortable', name: 'Comfortable', desc: 'Roomy spacing, larger tap targets' },
  { id: 'compact', name: 'Compact', desc: 'More rows on screen — good for HR and payroll work' },
];
export const NAV_STYLES = [
  { id: 'plain', name: 'Plain', desc: 'Neutral sidebar' },
  { id: 'tinted', name: 'Tinted', desc: 'Sidebar washed in your accent' },
];

const LIGHT_RAMP = { 50: [0.55, 96], 100: [0.7, 91], 200: [0.8, 83], 300: [0.9, 72], 400: [1, 62], 500: [1, 52], 600: [1, 45], 700: [1, 38], 800: [1, 31] };
const DARK_RAMP = { 50: [0.45, 15], 100: [0.5, 20], 200: [0.55, 26], 300: [0.8, 40], 400: [0.95, 58], 500: [1, 54], 600: [0.95, 63], 700: [0.85, 73], 800: [0.8, 81] };

function hslToRgb(h, s, l) {
  s /= 100; l /= 100;
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
}
function luminance(rgb) {
  const c = rgb.map((v) => { const x = v / 255; return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}
const contrast = (a, b) => {
  const l1 = luminance(a); const l2 = luminance(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
};

function tuneStep(h, s, l) {
  for (let i = 0; i < 32; i += 1) {
    const rgb = hslToRgb(h, s, l);
    const white = contrast(rgb, [255, 255, 255]);
    const dark = contrast(rgb, [20, 20, 20]);
    if (Math.max(white, dark) >= 4.65) return l;
    l += white > dark ? -2 : 2;
    if (l < 8 || l > 92) break;
  }
  return l;
}
function tuneInk(h, s, bgS, bgL, dark) {
  const bg = hslToRgb(h, bgS, bgL);
  let l = dark ? 66 : 40;
  const step = dark ? 2 : -2;
  for (let i = 0; i < 44; i += 1) {
    if (contrast(hslToRgb(h, s, l), bg) >= 4.6) return `hsl(${h} ${s}% ${l}%)`;
    l += step;
    if (l < 6 || l > 97) break;
  }
  return dark ? '#ffffff' : '#0f1115';
}
const inkFor = (h, s, l) => {
  const rgb = hslToRgb(h, s, l);
  return contrast(rgb, [255, 255, 255]) >= contrast(rgb, [20, 20, 20]) ? '#ffffff' : '#141414';
};

const DEFAULT = { accent: 'sevenmentor', mode: 'light', density: 'comfortable', navStyle: 'tinted' };
const ThemeContext = createContext(null);

export function applyTheme({ accent, mode, density, navStyle }) {
  const theme = THEMES.find((t) => t.id === accent) ?? THEMES[0];
  const resolved = mode === 'system'
    ? (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : mode;
  const dark = resolved === 'dark';
  const ramp = dark ? DARK_RAMP : LIGHT_RAMP;
  const root = document.documentElement;

  const [sm500, rawL500] = ramp[500];
  const l500 = tuneStep(theme.h, theme.s * sm500, rawL500);
  for (const [key, [sm, l]] of Object.entries(ramp)) {
    root.style.setProperty(`--brand-${key}`, `hsl(${theme.h} ${(theme.s * sm).toFixed(1)}% ${Number(key) === 500 ? l500 : l}%)`);
  }
  root.style.setProperty('--on-brand', inkFor(theme.h, theme.s * sm500, l500));
  const [sm700, l700] = ramp[700];
  root.style.setProperty('--on-brand-deep', inkFor(theme.h, theme.s * sm700, l700));

  const [smBg, lBg] = ramp[50];
  root.style.setProperty('--brand-ink', tuneInk(theme.h, theme.s, theme.s * smBg, lBg, dark));
  root.style.setProperty('--brand-ring', `hsl(${theme.h} ${theme.s}% 50% / .16)`);
  root.style.setProperty('--brand-glow', `hsl(${theme.h} ${theme.s}% 50% / .30)`);
  root.style.setProperty('--brand-wash', dark ? `hsl(${theme.h} ${theme.s * 0.4}% 12%)` : `hsl(${theme.h} ${theme.s * 0.55}% 97.5%)`);

  // A trace of the accent in the page and hover surfaces, so the whole app reads
  // as one colour family instead of jumping between white and grey.
  if (dark) {
    root.style.setProperty('--bg', `hsl(${theme.h} ${(theme.s * 0.16).toFixed(1)}% 6.5%)`);
    root.style.setProperty('--surface', `hsl(${theme.h} ${(theme.s * 0.12).toFixed(1)}% 10.5%)`);
    root.style.setProperty('--surface-2', `hsl(${theme.h} ${(theme.s * 0.12).toFixed(1)}% 12.5%)`);
    root.style.setProperty('--surface-3', `hsl(${theme.h} ${(theme.s * 0.13).toFixed(1)}% 15.5%)`);
    root.style.setProperty('--border', `hsl(${theme.h} ${(theme.s * 0.12).toFixed(1)}% 19%)`);
    root.style.setProperty('--border-strong', `hsl(${theme.h} ${(theme.s * 0.12).toFixed(1)}% 26%)`);
  } else {
    root.style.setProperty('--bg', `hsl(${theme.h} ${(theme.s * 0.3).toFixed(1)}% 96.5%)`);
    root.style.setProperty('--surface', '#ffffff');
    root.style.setProperty('--surface-2', `hsl(${theme.h} ${(theme.s * 0.26).toFixed(1)}% 98.6%)`);
    root.style.setProperty('--surface-3', `hsl(${theme.h} ${(theme.s * 0.24).toFixed(1)}% 95.4%)`);
    root.style.setProperty('--border', `hsl(${theme.h} ${(theme.s * 0.18).toFixed(1)}% 90.5%)`);
    root.style.setProperty('--border-strong', `hsl(${theme.h} ${(theme.s * 0.18).toFixed(1)}% 83%)`);
  }

  root.setAttribute('data-theme', resolved);
  root.setAttribute('data-density', density);
  root.setAttribute('data-nav', navStyle);
  root.setAttribute('data-accent', theme.id);
}

export function ThemeProvider({ children, initial }) {
  const [appearance, setAppearance] = useState({ ...DEFAULT, ...(initial ?? {}) });

  useEffect(() => { applyTheme(appearance); }, [appearance]);

  useEffect(() => {
    if (appearance.mode !== 'system' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme(appearance);
    query.addEventListener('change', handler);
    return () => query.removeEventListener('change', handler);
  }, [appearance]);

  const update = useCallback(async (patch) => {
    const next = { ...appearance, ...patch };
    setAppearance(next);
    // Optimistic: the theme is applied immediately and saved in the background.
    // A failed save is not worth interrupting somebody for.
    try { await put('/employees/me/appearance', next); } catch { /* keep the local change */ }
  }, [appearance]);

  const value = useMemo(() => ({ appearance, setAppearance, update, themes: THEMES }), [appearance, update]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}

/** One step of a theme's ramp, for swatches and previews outside the live tokens. */
export function swatchFor(theme, dark = false, step = 500) {
  const [sm, l] = (dark ? DARK_RAMP : LIGHT_RAMP)[step] ?? (dark ? DARK_RAMP : LIGHT_RAMP)[500];
  return `hsl(${theme.h} ${(theme.s * sm).toFixed(1)}% ${l}%)`;
}
